import { RouteObject, Navigate, useParams } from 'react-router-dom';
import { RequireAuth, RequireWorkspace, RequireRole } from './guards';
import { lazyWithRetry as lazy } from '@/lib/lazy-with-retry';
// ^ alias keeps the existing `lazy(() => import(...))` call sites
//   unchanged while gaining the auto-reload-on-stale-chunk behaviour.
import { useIsMobile } from '@/hooks/useIsMobile';

// Helper component for redirects with params
const RedirectWithParams = ({ basePath }: { basePath: string }) => {
  const params = useParams();
  const path = Object.entries(params).reduce(
    (acc, [key, value]) => acc.replace(`:${key}`, value || ''),
    basePath
  );
  return <Navigate to={path} replace />;
};

// ============================================================
// N71 NAVIGATOR-FIRST ARCHITECTURE
// All functionality flows through the N71 Navigator interface
// Legacy role-based dashboards redirect to /n71
// ============================================================

// Core pages
const Landing = lazy(() => import('@/pages/Landing'));
const Auth = lazy(() => import('@/pages/Auth'));
const Waitlist = lazy(() => import('@/pages/Waitlist'));
const N71 = lazy(() => import('@/pages/N71'));
const N71Learnings = lazy(() => import('@/pages/N71Learnings'));
const LinkWhatsApp = lazy(() => import('@/pages/LinkWhatsApp'));
const Admin = lazy(() => import('@/pages/Admin'));

// Internal admin portal (workspace health) — gated by @n71.ai / @dtek.ai SSO
const AdminGate = lazy(() => import('@/pages/admin/AdminGate').then(m => ({ default: m.AdminGate })));
const AdminWorkspaceList = lazy(() => import('@/pages/admin/WorkspaceList'));
const AdminWorkspaceDetail = lazy(() => import('@/pages/admin/WorkspaceDetail'));

// Essential utility pages
const InviteAccept = lazy(() => import('@/pages/InviteAccept'));
const UserOnboarding = lazy(() => import('@/pages/UserOnboarding'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const NoWorkspace = lazy(() => import('@/pages/NoWorkspace'));
const AwaitingRole = lazy(() => import('@/pages/AwaitingRole'));
const ProcessPendingOAuth = lazy(() => import('@/pages/ProcessPendingOAuth'));

// OAuth callbacks
const SlackCallback = lazy(() => import('@/pages/SlackCallback'));
const NotionCallback = lazy(() => import('@/pages/NotionCallback'));
const TeamsCallback = lazy(() => import('@/pages/TeamsCallback'));
const GoogleCallback = lazy(() => import('@/pages/GoogleCallback'));
const GoogleChatCallback = lazy(() => import('@/pages/GoogleChatCallback'));
const OAuthCallback = lazy(() => import('@/components/OAuthCallback'));

// Team invite (public pages for team members to authorize their accounts - no account needed)
const TeamInvite = lazy(() => import('@/pages/TeamInvite'));
const TeamInviteSuccess = lazy(() => import('@/pages/TeamInviteSuccess'));

// Rotating join code (new unified join-by-code page; supersedes InviteAccept +
// TeamInvite at cutover, both kept live for now). Requires auth, redirects to
// /auth?mode=signup&invite=1 when signed out, then calls the team-join fn.
const JoinTeam = lazy(() => import('@/pages/JoinTeam'));

// Leader invite (for department/functional leaders with scoped access)
const LeaderInvite = lazy(() => import('@/pages/LeaderInvite'));

// Onboarding - DEPRECATED: Now handled inline in Navigator
// Keeping imports for backwards compatibility redirects
// const OnboardingFlow = lazy(() => import('@/pages/onboarding/OnboardingFlow').then(m => ({ default: m.OnboardingFlow })));
// const FirstBriefing = lazy(() => import('@/pages/onboarding/FirstBriefing').then(m => ({ default: m.FirstBriefing })));

// Essential workspace management (admin only)
const N71Integrations = lazy(() => import('@/pages/N71Integrations'));
const WorkspaceTeam = lazy(() => import('@/pages/workspace/Team'));
// WorkspaceSettings DEPRECATED - preferences now in N71PreferencesPanel
const WorkspaceBilling = lazy(() => import('@/pages/workspace/Billing'));

// Advisory Board (standalone product — auth required, no workspace)
const AdvisoryBoard = lazy(() => import('@/pages/AdvisoryBoard'));

// Context — Product landing page + authenticated flows
const ContextLanding = lazy(() => import('@/pages/ContextLanding'));
const Developers = lazy(() => import('@/pages/Developers'));
// /doers retired 2026-05-26 — marketing surface removed per user direction.
// File apps/web/src/pages/ForDoers.tsx kept on disk for easy revival.
// const ForDoers = lazy(() => import('@/pages/ForDoers'));
const ContextOnboarding = lazy(() => import('@/pages/ContextOnboarding'));

// Developer Portal — separate auth, separate nav
const DevLayout = lazy(() => import('@/pages/developers/DevLayout'));
const DevLanding = lazy(() => import('@/pages/developers/DevLanding'));
const DevSignup = lazy(() => import('@/pages/developers/DevSignup'));
const DevLogin = lazy(() => import('@/pages/developers/DevLogin'));
const DevDashboard = lazy(() => import('@/pages/developers/DevDashboard'));
const DevKeys = lazy(() => import('@/pages/developers/DevKeys'));
const DevUsage = lazy(() => import('@/pages/developers/DevUsage'));
const DevGraphs = lazy(() => import('@/pages/developers/DevGraphs'));
const DevGraphDetail = lazy(() => import('@/pages/developers/DevGraphDetail'));
const DevDocs = lazy(() => import('@/pages/developers/DevDocs'));
const ContextDashboard = lazy(() => import('@/pages/ContextDashboard'));
// Mobile shell — a focused phone-first experience (Chat / Machines /
// Settings). DashboardEntry below renders it on phone-sized viewports.
const MobileApp = lazy(() => import('@/components/mobile/MobileApp'));
const ContextSettings = lazy(() => import('@/pages/ContextSettings'));
const ContextAdmin = lazy(() => import('@/pages/ContextAdmin'));
const ContextAuthorize = lazy(() => import('@/pages/ContextAuthorize'));
const CliPair = lazy(() => import('@/pages/CliPair'));
const WorkMonitor = lazy(() => import('@/pages/WorkMonitor'));
const TrackingDashboard = lazy(() => import('@/pages/TrackingDashboard'));

// Brain — Developer-facing MCP dashboard
const BrainLanding = lazy(() => import('@/pages/brain/BrainLanding'));
const BrainDashboard = lazy(() => import('@/pages/brain/BrainDashboard'));
const BrainDocs = lazy(() => import('@/pages/brain/BrainDocs'));

// Shared pages (public branded URLs)
const SharedPage = lazy(() => import('@/pages/SharedPage'));

// Pricing & Legal (public)
const Pricing = lazy(() => import('@/pages/Pricing'));
const Terms = lazy(() => import('@/pages/Terms'));
const Privacy = lazy(() => import('@/pages/Privacy'));

// Ministry of Health edition — white-label tenant. Routes resolve under
// /mohap/* (path-prefix routing — same origin serves all tenants;
// branded domains, if a customer demands one in production, become a
// CNAME + tiny middleware tweak, not an architecture change). Gated
// by edition detection + workspace membership + role assignment
// inside MinistryShell. MinistryRedirectGate wraps the standard
// product landing so a /mohap path-prefix load goes straight to the
// Ministry shell.
import { ministryRoutes } from '@/ministry/routes';
import { MinistryRedirectGate } from '@/ministry/MinistryRedirectGate';
// Elias edition — single-user, frontier-default, document-grounded.
// Routes resolve under /elias/*. Same path-prefix routing pattern as
// Ministry; gated by edition detection + workspace membership inside
// EliasShell. Backed by the n71-elias-worker Fly app for traffic
// isolation (fly.elias.toml).
import { eliasRoutes } from '@/elias/routes';
import { hub71Routes } from '@/hub71/routes';

// ============================================================
// DEPRECATED IMPORTS - Keeping for reference during migration
// These pages are being phased out - all functionality is in Navigator
// ============================================================
// const CoordinationOS = lazy(() => import('@/pages/os/CoordinationOS'));
// const FounderOS = lazy(() => import('@/pages/os/FounderOS'));
// const EngineeringOS = lazy(() => import('@/pages/os/EngineeringOS'));
// const ProductOS = lazy(() => import('@/pages/os/ProductOS'));
// const SalesOS = lazy(() => import('@/pages/os/SalesOS'));
// const FinanceOS = lazy(() => import('@/pages/os/FinanceOS'));
// const EmployeeDashboardLayout = lazy(() => import('@/pages/employee-dashboard'));
// const ManagerDashboardLayout = lazy(() => import('@/pages/ManagerDashboard'));
// const ExecutiveDashboardLayout = lazy(() => import('@/pages/executive-dashboard'));
// See DEPRECATION.md for full list

/**
 * DashboardEntry — the /context/dashboard mount point.
 *
 * On phone-sized / touch viewports it renders the focused mobile shell
 * (MobileApp); on desktop it renders the full ContextDashboard. Picking
 * here, behind the route, means every link and redirect that lands on
 * /context/dashboard "just works" on a phone — no separate /m URL.
 */
function DashboardEntry() {
  const { isMobile } = useIsMobile();
  return isMobile ? <MobileApp /> : <ContextDashboard />;
}

export const routes: RouteObject[] = [
  // Public routes — Ministry edition pre-empts the standard landing.
  // (gov.n71.ai serves the static gov-landing.html at "/" via nginx, so this
  // SPA "/" route is only reached on non-sovereign builds + client-side nav.)
  { path: '/', element: <MinistryRedirectGate><Landing /></MinistryRedirectGate> },
  // Shared pages — public branded URLs (no auth required)
  { path: '/s/:shortId', element: <SharedPage /> },
  { path: '/pricing', element: <Pricing /> },
  // /research is a STATIC page (public/research/) served by Netlify
  // before the SPA catch-all — no route here. Links to it must be
  // plain <a href> full-page loads, not router navigation.
  { path: '/terms', element: <Terms /> },
  // Canonical privacy policy URL (Google verification expects this to be easy to find)
  { path: '/privacy-policy', element: <Privacy /> },
  // Backwards compatibility for older links/bookmarks
  { path: '/privacy', element: <Navigate to="/privacy-policy" replace /> },
  // Direct sign-up disabled — every signup CTA lands on the waitlist.
  // /start, /auth?mode=signup, /signup all redirect here. Existing users
  // continue to log in via /auth (mode=login is the only allowed mode).
  { path: '/waitlist', element: <Waitlist /> },
  { path: '/start', element: <Navigate to="/waitlist" replace /> },
  { path: '/signup', element: <Navigate to="/waitlist" replace /> },
  { path: '/auth', element: <Auth /> },
  { path: '/reset-password', element: <ResetPassword /> },
  
  // Developer Portal — own auth system (DeveloperAuthProvider), not Supabase
  {
    element: <DevLayout />,
    children: [
      { path: '/developers', element: <DevLanding /> },
      { path: '/developers/signup', element: <DevSignup /> },
      { path: '/developers/login', element: <DevLogin /> },
      { path: '/developers/dashboard', element: <DevDashboard /> },
      { path: '/developers/keys', element: <DevKeys /> },
      { path: '/developers/usage', element: <DevUsage /> },
      { path: '/developers/graphs', element: <DevGraphs /> },
      { path: '/developers/graphs/:id', element: <DevGraphDetail /> },
      { path: '/developers/docs', element: <DevDocs /> },
      { path: '/developers/docs/:section', element: <DevDocs /> },
    ],
  },

  // Context — redirects to homepage (backward compat)
  { path: '/context', element: <Navigate to="/" replace /> },

  // n71 CLI device-code pairing landing — opened by `n71 login` in
  // the user's terminal. Authenticates inline (CliPair handles
  // unauthenticated → /auth bounce); intentionally NOT inside
  // RequireWorkspace because the page itself is the workspace
  // selector for the pairing.
  { path: '/cli/:code', element: <CliPair /> },

  // /doers retired 2026-05-26 — marketing page removed.

  // Brain — public landing + docs
  { path: '/brain', element: <BrainLanding /> },
  { path: '/brain/docs', element: <BrainDocs /> },

  // Admin dashboard (has its own password protection)
  { path: '/admin', element: <Admin /> },

  // Internal admin portal — workspace health, gated to @n71.ai / @dtek.ai
  {
    element: <AdminGate />,
    children: [
      { path: '/admin/workspaces', element: <AdminWorkspaceList /> },
      { path: '/admin/workspaces/:workspaceId', element: <AdminWorkspaceDetail /> },
    ],
  },
  // Advisory Board — standalone product (handles its own auth inline)
  { path: '/advisoryboard', element: <AdvisoryBoard /> },
  // Hub71 helpdesk demo — PUBLIC, anonymous, iframe-embedded from
  // assist.hub71.com. Sibling of <RequireAuth>; the route handles its
  // own (zero-auth) flow. Per-route Netlify CSP (E6) relaxes
  // frame-ancestors so the iframe doesn't get blocked.
  ...hub71Routes,
  // Redirects for old invite URLs (backwards compatibility)
  { path: '/join/:code', element: <RedirectWithParams basePath="/n71/join/:code" /> },
  { path: '/invite/:code', element: <RedirectWithParams basePath="/n71/invite/:code" /> },
  { path: '/leader-invite', element: <Navigate to="/n71/leader-invite" replace /> },
  { path: '/team-invite/:workspaceId/:code', element: <RedirectWithParams basePath="/n71/team-invite/:workspaceId/:code" /> },
  { path: '/team-invite/:workspaceId', element: <RedirectWithParams basePath="/n71/team-invite/:workspaceId" /> },
  
  { path: '/user-onboarding', element: <UserOnboarding /> },
  
  // Onboarding - DEPRECATED: Redirect to Navigator
  // First-run onboarding lives at /context/onboarding (2-step flow:
  // Connect tools → Build your brain). Auth.tsx routes new sign-ins
  // here via /onboarding when has_seen_onboarding is false and no
  // workspace exists.
  {
    element: <RequireAuth />,
    children: [
      { path: '/onboarding', element: <Navigate to="/context/onboarding" replace /> },
      { path: '/onboarding/complete', element: <Navigate to="/context/dashboard" replace /> },
    ]
  },

  // Ministry of Health edition — gated behind RequireAuth so only
  // signed-in users hit the shell. Workspace-membership + role
  // gating happens inside MinistryShell.
  {
    element: <RequireAuth />,
    children: ministryRoutes,
  },

  // Elias edition — gated behind RequireAuth so only signed-in users
  // hit the shell. Workspace-membership gating happens inside
  // EliasShell. Single seeded role ("operator") so no role guard.
  {
    element: <RequireAuth />,
    children: eliasRoutes,
  },
  
  // OS Module Pages - DEPRECATED: Redirect to N71
  // These were marketing pages, now all functionality is in Navigator
  { path: '/coordination-os', element: <Navigate to="/context/dashboard" replace /> },
  { path: '/founder-os', element: <Navigate to="/context/dashboard" replace /> },
  { path: '/engineering-os', element: <Navigate to="/context/dashboard" replace /> },
  { path: '/product-os', element: <Navigate to="/context/dashboard" replace /> },
  { path: '/growth-os', element: <Navigate to="/context/dashboard" replace /> },
  { path: '/finance-os', element: <Navigate to="/context/dashboard" replace /> },
  
  // OAuth callbacks
  { path: '/auth/slack/callback', element: <SlackCallback /> },
  { path: '/auth/notion/callback', element: <NotionCallback /> },
  { path: '/auth/teams/callback', element: <TeamsCallback /> },
  { path: '/auth/google/callback', element: <GoogleCallback /> },
  { path: '/auth/googlechat/callback', element: <GoogleChatCallback /> },
  { path: '/auth/github/callback', element: <OAuthCallback provider="github" /> },
  { path: '/auth/jira/callback', element: <OAuthCallback provider="jira" /> },
  { path: '/auth/linear/callback', element: <OAuthCallback provider="linear" /> },
  { path: '/auth/outlook/callback', element: <OAuthCallback provider="outlook" /> },
  { path: '/auth/asana/callback', element: <OAuthCallback provider="asana" /> },
  // B2C integrations
  { path: '/auth/shopify/callback', element: <OAuthCallback provider="shopify" /> },
  { path: '/auth/gbp/callback', element: <OAuthCallback provider="gbp" /> },
  // CRM & Data integrations
  { path: '/auth/hubspot/callback', element: <OAuthCallback provider="hubspot" /> },
  { path: '/auth/airtable/callback', element: <OAuthCallback provider="airtable" /> },
  // Project management
  { path: '/auth/clickup/callback', element: <OAuthCallback provider="clickup" /> },
  // Atlassian
  { path: '/auth/confluence/callback', element: <OAuthCallback provider="confluence" /> },
  // Developer integrations
  { path: '/auth/supabase/callback', element: <OAuthCallback provider="supabase" /> },
  // MCP integrations (Granola, Fireflies — shared callback)
  { path: '/auth/mcp/callback', element: <OAuthCallback provider="mcp" /> },
  
  // Workspace redirect
  { path: '/workspace', element: <Navigate to="/no-workspace" replace /> },
  
  // Billing shortcut (redirects to workspace billing)
  { path: '/billing', element: <Navigate to="/context/dashboard" replace /> },
  
  
  // WhatsApp linking - public but redirects to auth if needed
  { path: '/link-whatsapp', element: <LinkWhatsApp /> },
  
  // Public invite routes under /n71 (production path)
  // New rotating-join-code page (the cutover target). Kept alongside the
  // legacy /n71/invite + /n71/team-invite routes below until cutover.
  { path: '/n71/join/:code', element: <JoinTeam /> },
  { path: '/n71/invite/:code', element: <InviteAccept /> },
  { path: '/n71/leader-invite', element: <LeaderInvite /> },
  { path: '/n71/team-invite/:workspaceId/:code', element: <TeamInvite /> },
  { path: '/n71/team-invite/:workspaceId', element: <TeamInvite /> },
  { path: '/n71/team-invite-success', element: <TeamInviteSuccess /> },
  
  // Protected routes
  {
    element: <RequireAuth />,
    children: [
      { path: '/no-workspace', element: <NoWorkspace /> },
      { path: '/process-oauth', element: <ProcessPendingOAuth /> },
      { path: '/awaiting-role', element: <AwaitingRole /> },
      // Setup/Onboarding - DEPRECATED: Redirect to Navigator
      { path: '/setup', element: <Navigate to="/context/dashboard" replace /> },
      { path: '/workspace/:workspaceId/setup', element: <Navigate to="/context/dashboard" replace /> },
      
      {
        element: <RequireWorkspace />,
        children: [
          // N71 - The primary interface (Navigator)
          // This is THE interface - everything flows through here
          { path: '/n71', element: <N71 /> },
          { path: '/n71/integrations', element: <N71Integrations /> },
          { path: '/n71/learnings', element: <N71Learnings /> },
          { path: '/brain/dashboard', element: <BrainDashboard /> },

          // Context product flows
          // Workflow monitoring surface (M6)
          { path: '/work', element: <WorkMonitor /> },

          // Public-health social-media surveillance (MoHAP). Workspace-
          // aware: MoHAP edition gets MoHAP-branded chrome, others get
          // default N71 chrome.
          { path: '/tracking', element: <TrackingDashboard /> },

          { path: '/context/onboarding', element: <ContextOnboarding /> },
          { path: '/context/dashboard', element: <DashboardEntry /> },
          { path: '/context/settings', element: <Navigate to="/context/dashboard?tab=settings" replace /> },
          { path: '/context/admin', element: <ContextAdmin /> },
          { path: '/context/authorize', element: <ContextAuthorize /> },
          
          // Redirects to Navigator for backwards compatibility
          { path: '/spark', element: <Navigate to="/context/dashboard" replace /> },
          { path: '/dashboard', element: <Navigate to="/context/dashboard" replace /> },
          
          // ============================================================
          // DEPRECATED ROLE-BASED DASHBOARDS
          // All functionality is now in Navigator (/n71)
          // These redirects maintain backwards compatibility
          // ============================================================
          
          // Employee routes - redirect to Navigator
          { path: '/employee', element: <Navigate to="/context/dashboard" replace /> },
          { path: '/employee/*', element: <Navigate to="/context/dashboard" replace /> },
          
          // Manager routes - redirect to Navigator
          { path: '/manager', element: <Navigate to="/context/dashboard" replace /> },
          { path: '/manager/*', element: <Navigate to="/context/dashboard" replace /> },
          
          // Executive routes - redirect to Navigator
          { path: '/executive', element: <Navigate to="/context/dashboard" replace /> },
          { path: '/executive/*', element: <Navigate to="/context/dashboard" replace /> },
          
          // Founder routes - redirect to Navigator
          { path: '/founder', element: <Navigate to="/context/dashboard" replace /> },
          { path: '/founder/*', element: <Navigate to="/context/dashboard" replace /> },
          
          // Workspace routes
          // Only essential admin pages remain, rest redirect to Navigator
          {
            path: '/workspace/:workspaceId',
            element: <RequireRole allowedRoles={['ic', 'manager', 'executive', 'admin']} />,
            children: [
              // Redirect index to Navigator
              { index: true, element: <Navigate to="/context/dashboard" replace /> },
              
              // Essential workspace management pages (KEEP)
              { path: 'integrations', element: <Navigate to="/n71/integrations" replace /> },
              { path: 'learnings', element: <Navigate to="/n71/learnings" replace /> },
              { path: 'team', element: <WorkspaceTeam /> },
              { path: 'settings', element: <Navigate to="/context/dashboard" replace /> }, // DEPRECATED - preferences now in N71PreferencesPanel
              { path: 'billing', element: <WorkspaceBilling /> },
              { path: 'onboarding', element: <Navigate to="/context/dashboard" replace /> },
              
              // DEPRECATED: Redirect to Navigator
              { path: 'dashboard', element: <Navigate to="/context/dashboard" replace /> },
              { path: 'overview', element: <Navigate to="/context/dashboard" replace /> },
              { path: 'schedule', element: <Navigate to="/context/dashboard" replace /> },
              { path: 'retrospectives', element: <Navigate to="/context/dashboard" replace /> },
              { path: 'analytics', element: <Navigate to="/context/dashboard" replace /> },
              { path: 'intelligence', element: <Navigate to="/context/dashboard" replace /> },
              { path: 'workgraph', element: <Navigate to="/context/dashboard" replace /> },
              { path: 'memory', element: <Navigate to="/context/dashboard" replace /> },
              { path: 'playbooks', element: <Navigate to="/context/dashboard" replace /> },
              { path: 'inbox', element: <Navigate to="/context/dashboard" replace /> },
              { path: 'admin', element: <Navigate to="/context/dashboard" replace /> },
              { path: 'command-center', element: <Navigate to="/context/dashboard" replace /> },
              { path: 'matrix', element: <Navigate to="/context/dashboard" replace /> }
            ]
          },
          
          // Matrix page - DEPRECATED: Redirect to Navigator
          { path: '/matrix/:matrixId', element: <Navigate to="/context/dashboard" replace /> }
        ]
      }
    ]
  }
];
